import React, { useState } from 'react';
import { useStyletron } from 'baseui';
import { FormControl } from 'baseui/form-control';
import { Heading, HeadingLevel } from 'baseui/heading';
import { ProgressSteps, Step } from 'baseui/progress-steps';
import { Select } from 'baseui/select';
import { toaster } from 'baseui/toast';
import { faPenToSquare } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createAssignmentMatchHints } from '@gtintel/platform-api';
import { NPRequestStatus } from '@gtintel/platform-types';
import { useAsyncOp, useDebounce } from '@gtintel/platform-utils';
import { Title } from '../../components/Title';
import { RequestEditorContext } from '../context';
import { Assign, Finished, Partner, ScopeSelection, Send, Wait } from './Steps';
import { getAssignStepName, getFinishedStepName, getSendStepName, getWaitStepName } from './utils';
const StatusSelectOptions = [{
  id: NPRequestStatus.Draft,
  label: 'Draft'
}, {
  id: NPRequestStatus.Sent,
  label: 'Sent'
}, {
  id: NPRequestStatus.Declined,
  label: 'Declined'
}, {
  id: NPRequestStatus.Accepted,
  label: 'Accepted'
}, {
  id: NPRequestStatus.Cancelled,
  label: 'Cancelled'
}, {
  id: NPRequestStatus.AcceptedCancelled,
  label: 'Accepted but Cancelled'
}, {
  id: NPRequestStatus.Assigned,
  label: 'Activated'
}, {
  id: NPRequestStatus.Submitted,
  label: 'Submitted'
}, {
  id: NPRequestStatus.Finished,
  label: 'Finished'
}];
const StatusChangeEffects = {
  [NPRequestStatus.Draft]: 'any assigned partner will be unable to access the assignment.',
  [NPRequestStatus.Sent]: 'an email will be sent to the partner with process document attached.',
  [NPRequestStatus.Declined]: 'if the assignment is a lead and the project is matched or above, the project set back to pending and an email sent to project CX lead.',
  [NPRequestStatus.Accepted]: 'if the assignment is a lead, the project will be matched. An email will be sent to project CX lead.',
  [NPRequestStatus.Cancelled]: 'if the assignment is a lead, the project will be set back to pending.',
  [NPRequestStatus.Assigned]: 'if the assignment is a lead, the project will be set to activated. An email will be sent to the partner.',
  [NPRequestStatus.AcceptedCancelled]: 'if the assignment is a lead, the project will be set back to pending.',
  [NPRequestStatus.Submitted]: 'an email will be sent to CX lead. If QA is required, an email is sent to the QAer',
  [NPRequestStatus.Finished]: 'an email will be sent to the partner.'
};
const RequestEditor = () => {
  const [css] = useStyletron();
  const ctx = React.useContext(RequestEditorContext);
  const waitStep = getWaitStepName(ctx.status());
  const assignStep = getAssignStepName(ctx.status());
  const sendStep = getSendStepName(ctx.status());
  const finishedStep = getFinishedStepName(ctx.status());
  const stepNames = ['Scope', 'Partner', sendStep, waitStep, assignStep, 'Finished'];
  const statusToStepName = status => {
    switch (status) {
      case NPRequestStatus.Draft:
        if (ctx.isScopeSelected()) {
          if (!ctx.partner()) {
            return 'Partner';
          }
          return 'Send';
        } else {
          return 'Scope';
        }
      case NPRequestStatus.Sent:
      case NPRequestStatus.Cancelled:
        return waitStep;
      case NPRequestStatus.Accepted:
      case NPRequestStatus.Assigned:
      case NPRequestStatus.AcceptedCancelled:
        return assignStep;
      case NPRequestStatus.Submitted:
      case NPRequestStatus.Finished:
        return finishedStep;
      case NPRequestStatus.Declined:
        return 'Partner';
    }
  };
  const initialStepName = () => {
    return statusToStepName(ctx.status());
  };
  const [stepName, setStepName] = React.useState(initialStepName());
  const [isMatchingPartners, setIsMatchingPartners] = useState(false);
  const [name, setName] = useState(ctx.name() || `Assignment #${ctx.requestId()}`);
  const canRequestPartnerMatches = !ctx.isSent() && !ctx.isQaAssignment() && (!ctx.proposedPartners() || ctx.proposedPartners().length === 0) && stepName === 'Scope';
  const advance = status => {
    if (canRequestPartnerMatches) {
      setIsMatchingPartners(true);
      createAssignmentMatchHints(ctx.requestId()).then(data => {
        ctx.addProposedPartners(data);
      }).catch(e => {
        console.error(e);
        toaster.negative('Failed to find matches. Contact an administrator.');
      }).finally(() => {
        setStepName(statusToStepName(status));
        setIsMatchingPartners(false);
      });
    } else {
      setStepName(statusToStepName(status));
    }
  };
  const {
    fn: transitionToSent
  } = useAsyncOp(ctx.send);
  const scope = [<ScopeSelection />];
  const partner = [<Partner editable />];
  const send = [<Send />];
  const wait = [<Wait />];
  const assign = [<Assign />];
  const finished = [<Finished />];
  const steps = {
    scope,
    partner,
    send,
    wait,
    accepted: wait,
    declined: wait,
    activate: assign,
    activated: assign,
    cancelled: assign,
    finished,
    finish: finished
  };
  const [main] = steps[stepName.toLowerCase()];
  const setAssignmentName = () => {
    ctx.setName(name);
  };
  const debouncedSetName = useDebounce(setAssignmentName, 500);
  return <div data-testid="request-editor" className={css({
    position: 'relative'
  })}>
      <HeadingLevel>
        <Heading>
          <div className={css({
          alignItems: 'center',
          display: 'flex',
          flexFlow: 'row',
          gap: '8px'
        })}>
            <Title text={name || 'Set name...'} suffix={`(ID: ${ctx.id()})`} onChange={name => {
            setName(name);
            debouncedSetName();
          }} overrides={{
            Root: {
              style: {
                margin: '0'
              }
            }
          }} />
            <FontAwesomeIcon icon={faPenToSquare} size="sm" />
          </div>
        </Heading>
      </HeadingLevel>
      <ProgressSteps current={stepNames.indexOf(stepName)} orientation="horizontal" overrides={{
      IconContainer: {
        style: {
          backgroundColor: 'transparent'
        }
      }
    }}>
        {stepNames.map(s => {
        const stepKey = s.toLowerCase();
        return <Step alwaysShowDescription key={stepKey} title={s} />;
      })}
      </ProgressSteps>
      <div className={css({
      alignContent: 'flex-end',
      display: 'flex',
      flexFlow: 'column',
      justifySelf: 'flex-end',
      margin: '20px 0 20px auto',
      minWidth: '200px',
      width: 'fit-content',
      '@media (max-width: 768px)': {
        width: '100%'
      }
    })}>
        <FormControl label={() => 'Status'}>
          <Select options={StatusSelectOptions} value={[StatusSelectOptions.find(option => option.id === ctx.status())]} multi={false} clearable={false} overrides={{
          ValueContainer: {
            props: {
              'data-testid': 'assignment-status-select'
            }
          }
        }} onChange={params => {
          if (params && params.value.length > 0) {
            // eslint-disable-next-line no-restricted-globals
            const approved = confirm(`You are about to move this assignment to ${params.value[0].label} and ${StatusChangeEffects[params.value[0].id]}`);
            if (approved) {
              if (params.value[0].id !== NPRequestStatus.Draft) {
                advance(params.value[0].id);
              } else {
                setStepName('Scope');
              }
              if (params.value[0].id === NPRequestStatus.Sent) {
                transitionToSent().then(() => {
                  console.info('Sending to partner');
                }).catch(e => {
                  console.error(e);
                });
              } else {
                switch (params.value[0].id) {
                  case NPRequestStatus.Draft:
                    ctx.setDraft();
                    break;
                  case NPRequestStatus.Assigned:
                    ctx.assign();
                    break;
                  case NPRequestStatus.Cancelled:
                    ctx.cancel();
                    break;
                  case NPRequestStatus.Submitted:
                    ctx.submit();
                    break;
                  case NPRequestStatus.Declined:
                    ctx.decline();
                    break;
                  case NPRequestStatus.Accepted:
                    ctx.accept();
                    break;
                  case NPRequestStatus.Finished:
                    ctx.toggleFinished();
                    break;
                  default:
                    console.error(`Unknown status selected ${params.value[0].label}`);
                }
              }
            }
          }
        }} isLoading={isMatchingPartners} />
        </FormControl>
      </div>
      {main}
    </div>;
};
export default RequestEditor;